import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { TaskModel, SubtaskModel, TaskMgmtService } from '../../service/task-mgmt.service';

export interface SubtaskDropConfirmPayload {
  subtask: SubtaskModel;
  parentTask: TaskModel;
  previousStatus: string;
  newStatus: string;
}

/** Emitted on every subtask drag-drop transition (no confirmation flow). */
export interface SubtaskMovePayload {
  subtask: SubtaskModel;
  previousStatus: string;
  newStatus: string;
}

interface MiniKanbanColumn {
  id: string;
  label: string;
  color: string;
  subtasks: SubtaskModel[];
}

@Component({
  selector: 'app-subtask-mini-kanban',
  templateUrl: './subtask-mini-kanban.component.html',
  styleUrls: ['./subtask-mini-kanban.component.scss'],
})
export class SubtaskMiniKanbanComponent implements OnChanges {
  @Input() parentTask!: TaskModel;
  /** When provided, the kanban distributes these subtasks instead of `parentTask.subtasks`.
   *  Used by the slide-in sidebar where subtasks are loaded separately via the API. */
  @Input() externalSubtasks: SubtaskModel[] | null = null;
  /** Plumbed from the drilldown panel so the compact-card child can resolve task type names. */
  @Input() taskTypeOptions: any[] = [];
  /** Emits a structured payload on every drag-drop transition (no confirmation). */
  @Output() subtaskMoved = new EventEmitter<SubtaskMovePayload>();
  @Output() subtaskClicked = new EventEmitter<SubtaskModel>();
  /** Emits the column id (status) where the user requested to add a subtask. */
  @Output() createSubtask = new EventEmitter<string>();
  /** Emits when the user clicks the comment badge on a subtask card. */
  @Output() subtaskCommentsClicked = new EventEmitter<SubtaskModel>();
  /** Emits when the user clicks the attachment badge on a subtask card. */
  @Output() subtaskAttachmentsClicked = new EventEmitter<SubtaskModel>();
  /** Kept for backwards compat with existing callers; no longer emitted from this component. */
  @Output() subtaskDropConfirmNeeded = new EventEmitter<SubtaskDropConfirmPayload>();

  columns: MiniKanbanColumn[] = [
    { id: 'st-planned', label: 'Open', color: '#1363df', subtasks: [] },
    { id: 'st-in-progress', label: 'In-Progress', color: '#f97316', subtasks: [] },
    { id: 'st-done', label: 'Completed', color: '#22c55e', subtasks: [] },
    { id: 'st-cancelled', label: 'Cancelled', color: '#ef4444', subtasks: [] },
  ];

  connectedLists: string[] = ['st-planned', 'st-in-progress', 'st-done', 'st-cancelled'];

  private statusMap: Record<string, string> = {
    'st-planned': 'planned',
    'st-in-progress': 'in-progress',
    'st-done': 'done',
    'st-cancelled': 'cancelled',
  };

  constructor(private taskService: TaskMgmtService) {}

  /** Convert a subtask's status (numeric API id, kanban-key string, or empty)
   *  into the matching kanban column key. Falls back to `_raw.task_status_name`
   *  when the model only has the id. */
  private subtaskColumnKey(st: any): string {
    if (!st) return 'planned';
    const rawName = (st._raw?.task_status_name || '').toLowerCase().trim();
    let name = rawName;
    if (!name && st.status != null && st.status !== '') {
      // If status is already a kanban key, use it directly.
      const sval = String(st.status);
      if (sval === 'planned' || sval === 'in-progress' || sval === 'done' || sval === 'cancelled') {
        return sval;
      }
      const looked = this.taskService.getStatusNameById(Number(st.status));
      name = (looked || '').toLowerCase().trim();
    }
    if (name === 'open') return 'planned';
    if (name === 'in progress' || name === 'inprogress' || name === 'in-progress') return 'in-progress';
    if (name === 'done' || name === 'closed' || name === 'complete' || name === 'completed') return 'done';
    if (name === 'cancelled' || name === 'canceled') return 'cancelled';
    return 'planned';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parentTask'] || changes['externalSubtasks']) {
      this.distributeSubtasks();
    }
  }

  private distributeSubtasks(): void {
    this.columns.forEach(col => col.subtasks = []);

    // Prefer external subtasks (loaded from API) when supplied, otherwise fall
    // back to the parent task's embedded subtasks.
    const source: SubtaskModel[] | undefined = this.externalSubtasks
      ?? this.parentTask?.subtasks;

    if (!source) return;

    source.forEach(st => {
      const colId = 'st-' + this.subtaskColumnKey(st);
      const col = this.columns.find(c => c.id === colId);
      if (col) {
        col.subtasks.push(st);
      } else {
        this.columns[0].subtasks.push(st);
      }
    });
  }

  onSubtaskDrop(event: CdkDragDrop<SubtaskModel[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }

    const previousColumnKey = this.statusMap[event.previousContainer.id] || 'planned';
    const newColumnKey = this.statusMap[event.container.id] || 'planned';

    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );

    const subtask: any = event.container.data[event.currentIndex];

    // Resolve the new column key to the API status id and name (so the model
    // stays consistent with the rest of the app: numeric id on `status`, name
    // on `_raw.task_status_name`).
    const aliases: Record<string, string[]> = {
      'planned': ['open'],
      'in-progress': ['in progress', 'inprogress', 'in-progress'],
      'done': ['done', 'closed', 'complete', 'completed'],
      'cancelled': ['cancelled', 'canceled'],
    };
    const candidates = aliases[newColumnKey] || [newColumnKey];
    let newStatusId: number | null = null;
    let newStatusName = '';
    for (const name of candidates) {
      const id = this.taskService.getStatusIdByName(name);
      if (id != null) {
        newStatusId = id;
        newStatusName = this.taskService.getStatusNameById(id);
        break;
      }
    }

    // Optimistic mutation — keep status as numeric id string, mirror name on _raw.
    if (newStatusId != null) {
      subtask.status = String(newStatusId);
      if (subtask._raw) {
        subtask._raw.task_status_name = newStatusName;
      } else {
        subtask._raw = { task_status_name: newStatusName };
      }
    }

    this.subtaskMoved.emit({
      subtask,
      previousStatus: previousColumnKey,
      newStatus: newColumnKey,
    });
  }

  onSubtaskCardClick(subtask: SubtaskModel, event?: Event): void {
    if (event) event.stopPropagation();
    this.subtaskClicked.emit(subtask);
  }

  /** Header "+" handler — emits the column's underlying status string. */
  onCreateSubtaskInColumn(columnId: string, event: Event): void {
    event.stopPropagation();
    const status = this.statusMap[columnId] || 'planned';
    this.createSubtask.emit(status);
  }

  /** Footer "+ Create Subtask" link in the Open column — kept for backwards compat. */
  onCreateSubtaskClick(event: Event): void {
    event.stopPropagation();
    this.createSubtask.emit('planned');
  }

  onSubtaskCommentsClick(subtask: SubtaskModel): void {
    this.subtaskCommentsClicked.emit(subtask);
  }

  onSubtaskAttachmentsClick(subtask: SubtaskModel): void {
    this.subtaskAttachmentsClicked.emit(subtask);
  }
}
